/**
 * useOverlay — THE ONE COMPOSABLE every custom-positioned body-level overlay
 * calls. It owns the LIFECYCLE the three modules under `composables/overlay/`
 * deliberately do not: when a surface enters the registry and leaves it, when
 * the engine places it and re-places it, what its root binds as style, and
 * which entry a surface nested inside another one calls its parent.
 *
 *   `overlayKinds.js`      says what a kind IS (band, traits, options, budget)
 *   `overlayRegistry.js`   says what is CURRENTLY open, and in what order
 *   `overlayPlacement.js`  says WHERE one overlay goes, pure over its inputs
 *   this module            says WHEN each of those is asked, and binds the answer
 *
 * A new pop-up is one roster line plus one call here (design D1):
 *
 *   const { style, side, arrowStyle, entry } = useOverlay({
 *     kind: 'inspector', open: isOpen, reference: probeRef, floating: panelRef,
 *     dismiss: close,
 *   })
 *   // <div ref="panelRef" :style="[style, ownExtras]">
 *
 * ── SIGNATURE ──────────────────────────────────────────────────────────────
 *
 *   useOverlay({ kind, open, reference, floating, parent, visible, dismiss,
 *                placement, budget, rect, options, active, escapeWhen, id })
 *
 *   kind       a roster name. Unknown → `kindFor` throws in development; in a
 *              production build it logs and this returns an INERT result (empty
 *              style, null entry), so the surface renders unregistered rather
 *              than at an invented band.
 *   open       MaybeRefOrGetter<boolean>, REQUIRED. The rising edge REGISTERS
 *              (synchronously — a keystroke in the same tick must see the new
 *              ledger, the registry's own rule), the falling edge DEREGISTERS,
 *              and so does disposal of the calling scope; deregistration is
 *              idempotent so both may fire. Registration IS openness (D6), so
 *              there is no implicitly-open registration and omitting `open`
 *              throws, exactly as `useEscapeDismiss` refuses an implicit `active`.
 *   reference  MaybeRefOrGetter of an Element or a VIRTUAL element
 *              (`{ getBoundingClientRect, contextElement? }`): the anchor. A
 *              placed kind is placed only once it has one; a reference that
 *              arrives later (a template ref) or changes identity (the cursor
 *              card's next reveal rect) re-attaches and places again. A virtual
 *              element whose rect MUTATES in place is re-read on every pass;
 *              the caller who mutates it calls `replace()`.
 *   floating   Ref<Element|null>: the node being placed — the surface's root.
 *              `null` for a surface nothing here positions (the map popup).
 *   parent     the ENTRY this surface was opened from, or a ref of one, or
 *              `null` for "none". Omitted, it is INJECTED — see the parent seam.
 *   visible    MaybeRefOrGetter<boolean>, default `true` (D5). See HIDDEN.
 *   dismiss    `() => void`: how the surface closes itself. Stored on the entry
 *              and read by BOTH the outside-click policies and the registry's
 *              Escape dispatcher, so a kind whose roster line carries
 *              `escape: true` is dismissable by this call alone — the adopter
 *              makes no `useEscapeDismiss` call of its own and cannot claim the
 *              keystroke twice.
 *   placement  MaybeRefOrGetter<string>: overrides the kind's primary placement.
 *   budget     the height budget: a function `(referenceRect, viewport, options)
 *              → number | null`, a roster token, or `null` for unbounded. Omitted,
 *              it is the KIND's — see THE BUDGET.
 *   rect       `() => DOMRect|null`: the entry's obstacle rect. Required in
 *              practice for a `placed: false` kind that is an obstacle (the map
 *              popup reads its map container); a placed kind defaults to the
 *              floating node's LIVE client rect, read at the moment another
 *              overlay places. It is the box the surface PAINTS, never its
 *              positioning wrapper, and `null` while it paints nothing (th-ilcxn).
 *   options    MaybeRefOrGetter<object>, merged OVER the kind's own options on
 *              every pass (`flip: false` drops the flip middleware — the held
 *              cursor card's re-clamp; `boundary`, a getter answering an
 *              Element, places the box inside it — the map popups' container).
 *              Not watched: a caller whose options change calls `replace()`.
 *   active     MaybeRefOrGetter<boolean>, default `true`. Forwarded to the
 *              registry, whose RISING edge promotes the entry to the end of the
 *              activation order, and RE-READ there at Escape dispatch time — so
 *              it carries both this surface's precedence and its authority to
 *              act on the keystroke. Most open overlays are active for their
 *              whole life and leave the default; two shapes do not. A surface
 *              that must re-activate WITHOUT closing (the Artemis floating
 *              input, whose re-`open()` onto a visible popover advances
 *              `openToken` and must promote — design D2) passes its own
 *              predicate and pulses it. A surface that is OPEN before it is
 *              DISMISSABLE (the three glance-card adapters: a card merely
 *              following the cursor must not take an Escape aimed at the surface
 *              underneath it) passes the narrower predicate, whose rising edge
 *              is the one moment it becomes both the top of the order and
 *              eligible for the key.
 *   escapeWhen MaybeRefOrGetter<boolean>|null, default `null` (always).
 *              Forwarded to the registry, which reads it at Escape dispatch
 *              time: an escape-bearing entry whose `escapeWhen` reads false is
 *              skipped and the keystroke goes to the next entry down. Unlike
 *              `active` it never promotes, so a surface keeps its place in the
 *              order while it declines the key — the tour player takes Escape
 *              only while its tour plays.
 *   id         a debugging handle for the entry; defaults to a serial.
 *
 * Returns `{ style, side, placement, arrowStyle, maxHeight, zIndex, depthClass,
 * entry, replace }` — refs and computeds, destructured by the adopter exactly as every
 * composable here is:
 *
 *   style       computed: `position: 'fixed'` for a placed kind; `top`/`left`
 *               in px ONLY once a pass has placed (never the origin — an
 *               unmeasured first frame renders unpositioned, the inspector's
 *               `null` contract); `maxHeight`/`maxWidth` in px when bounded;
 *               `minHeight` in px for a kind declaring `readableHeight` (that
 *               height, never more than `maxHeight`);
 *               `zIndex` from `zIndexFor(entry)`; and the HIDDEN pair while
 *               `visible` reads false. A `placed: false` kind's style carries
 *               `zIndex` alone (plus the hidden pair).
 *   side        computed: the engine's side (`'top' | 'bottom' | 'right' |
 *               'left'`), or `null` before a pass has placed.
 *   placement   computed: the WHOLE placement token the pass landed on
 *               (`'bottom-start'`, `'left'`, …), or `null` before one has. The
 *               side is that token's first segment, so `side` answers "which
 *               way from the anchor" and this answers "which candidate" —
 *               `bottom-start` and `bottom-end` are one side and two
 *               placements, and only the token distinguishes them. What reads
 *               it is a surface that must LAND SOMEWHERE and then STAY there:
 *               the cursor adapter records this while following and feeds it
 *               back as its `placement` on every held pass, so a card the
 *               fallback list sidestepped keeps that sidestep once the viewer
 *               holds it (`side` would lose the alignment, and re-deriving the
 *               candidate from coordinates would be a second placement engine).
 *   arrowStyle  computed: `{ x, left }` for the one kind with an arrow — `x` the
 *               NUMBER `EntityGlanceTooltip` takes as `arrow-offset`, `left` the
 *               same value as CSS — or `null`.
 *   maxHeight   computed: the height budget as a NUMBER — the same value
 *               `style.maxHeight` spells in px — or `null` when nothing caps
 *               this surface (an unbounded kind, a `placed: false` one, or no
 *               reference yet). The cap has TWO readers: this root, which binds
 *               it as CSS, and the `GlanceCard` inside the two card adapters,
 *               which scrolls its sections inside it through
 *               `GLANCE_CARD_HEIGHT_BUDGET`. Returning the number gives both
 *               ONE author, so the box the placement was chosen against and the
 *               box the card scrolls inside cannot disagree — including the
 *               tighter cap `avoidOverlays` reports beside an open panel. An
 *               adapter that parsed the px string back out was the second
 *               author this replaces.
 *   zIndex      computed: the entry's z, `null` while closed or band-less.
 *   depthClass  computed: the shadow class the kind's band names in the
 *               roster's `BAND_DEPTH` (`'shadow-lg'`, `'shadow-xl'`), or `null`
 *               for a band that casts none and for a band-less kind. The host
 *               binds it on its root and spells no shadow of its own. It
 *               follows the KIND, not openness, so a surface keeps its depth
 *               through its leave transition.
 *   entry       shallowRef: the registry entry while open, `null` otherwise. The
 *               map adapter hands `entry.value` to `touch`; a caller that opens a
 *               surface from this one may pass it as that surface's `parent`.
 *   replace     `() => Promise<void>`: re-runs one placement pass.
 *
 * ── THE PARENT SEAM: `OVERLAY_PARENT_ENTRY` ─────────────────────────────────
 *
 * `provide`/`inject` are setup-only, but an entry exists only while its surface
 * is open. So what is provided is not the entry but the HOLDER: the returned
 * `entry` shallowRef itself, under `OVERLAY_PARENT_ENTRY`. A descendant that
 * calls `useOverlay` injects that holder at setup and reads `.value` at ITS
 * registration time, so it names whatever entry its host holds at the moment it
 * opens. That is the whole mechanism — no host plumbing, no prop threading:
 * the inspector inside a glance card gets the card's entry; the citation hover
 * card inside the inspector gets the inspector's; th-pypli D's citation preview
 * card adopts the same key. `inject` reads the PARENT chain, so a component
 * that both injects and provides (the inspector) names its own host as parent
 * and hands its own entry to its descendants.
 *
 * ONLY A KIND THAT CAN HOST PROVIDES. Every kind INJECTS — a cue drawn over a
 * panel is still that panel's child, which is what `avoidsParent` reads — but
 * the holder goes DOWN only where the roster's `hosts` is true, meaning another
 * overlay's component tree can render inside this surface. A cue, a chrome bar,
 * a guidance surface and a tooltip host nothing, and their adopters are
 * routinely ancestors of most of the page: the workspace shell adopts the
 * Artemis bow, so without the trait every inspector and every card opened while
 * a hold ran would name a pointer ornament its parent — and `avoidOverlays`
 * and the Escape order both read `parent`. A non-hosting kind provides nothing
 * at all, so its descendants inherit the nearest HOSTING ancestor above it,
 * exactly as they would if it were not an overlay. Rejected: threading `parent`
 * through props (every intermediate component would carry it) and reading the
 * persistence injection (Critical 5: it does not reach the inspector). The seam
 * is also the ONLY relation a panel has to a card: the card's pointer-leave
 * claim holds the cards in a claimant's parent chain, and a superseding panel
 * spares exactly those (see ONE FLOATING PANEL AT A TIME). Outside a component
 * instance (a bare `effectScope`) nothing is injected or provided and `parent`
 * is what the caller passed, or `null`.
 *
 * ── ONE FLOATING PANEL AT A TIME (th-kxn6c.12.10) ──────────────────────────
 *
 * A kind whose roster line carries `supersedesCards` (the inspector) closes
 * every open card it was not opened from, at the rising edge of `open`: the
 * registry's `supersedeCards(entry)` runs each card's own `dismiss` over a
 * snapshot of the ledger. It runs after registration and BEFORE `entry` is
 * published, because publishing is what attaches and places — a panel placed
 * first would land clear of a card that is already closing. A card hovered
 * after the panel opened is not in the snapshot; it closes on its own leave,
 * because the panel's `holdsCards` reaches only its own parent chain.
 *
 * ── THE BUDGET IS A PRE-PASS, APPLIED BEFORE MEASUREMENT ───────────────────
 *
 * The height budget is chosen BY KIND through the roster's `budget` token
 * (lead decision 1): this module maps `'roomier-side'`, `'lane'` and
 * `'viewport'` to the engine's three pre-pass functions and `null` to
 * unbounded; a call-site `budget` (a function, a token, or `null`) overrides it.
 * `useOverlay` resolves it ONCE, at the call.
 *
 * The budget is bound BEFORE the first measurement (lead decision 2), and that
 * ordering is the reason it is a pre-pass at all: `placeOverlay` answers `null`
 * on the first pass because a box that has not laid out measures 0×0, but
 * `computeBudget` is pure over the reference rect and the viewport, so
 * `style.maxHeight` is bound from it the moment the surface opens. The DOM caps
 * the box; the second pass, after `nextTick`, measures the CAPPED box; and
 * `flip` then decides against the height that will actually paint, not the
 * unbounded one. Without the pre-pass the first measured height decides the
 * flip and the flip decides the height — the 2026-08-14 circularity. A kind
 * with a readable minimum (the Peek) is capped at the side the engine decides
 * from the anchor alone (`readableSide`), so no measured height moves its side.
 *
 * ── THE TWO-PASS FIRST FRAME, AND WHY THERE IS A PASS TOKEN ────────────────
 *
 * The engine places twice on open: once IMMEDIATELY, once after `nextTick`.
 * `subscribeAutoUpdate` calls its `onUpdate` once on subscribe, so for a kind
 * with `autoUpdate` that call IS pass one and nothing is added on top of it;
 * a kind with `autoUpdate: false` (the floating input) gets pass one from this
 * module directly. Pass two is `nextTick().then(replace)`. Steady state is
 * `autoUpdate`'s own ticks and the registry's `overlayVersion` (below).
 *
 * `computePosition` is a Promise in the installed 1.1.1, so "immediately on
 * open" means the pass is ISSUED synchronously and its result lands in a
 * microtask (lead decision 3). Every pass therefore takes a token, and a
 * resolution whose token is no longer the latest — an older pass landing after
 * a newer one, or after the surface closed or re-attached — is dropped: the
 * last pass issued is the only one that may write.
 *
 * A pass that measures nothing after a placement already exists leaves that
 * placement standing: a box mid-swap measures 0×0 for a frame, and "unpositioned"
 * is the right answer for a first frame, not for a surface the viewer is
 * looking at.
 *
 * ── A TELEPORTED REF CYCLES null → THE SAME NODE, AND THAT IS NOT A SWAP ───
 *
 * Vue nulls and re-sets a TELEPORTED node's template ref on every patch of the
 * host — the inspector's `panelRef` cycles `null` → the same DIV once per
 * re-render. The attachment watcher below is `flush: 'sync'`, so read literally
 * that is a detach and a re-attach per patch: the per-kind policies torn down
 * and re-installed (the 'panel' outside-click losing its macrotask arm and
 * taking a fresh one), `placed` nulled and re-placed so the style loses its
 * `top`/`left` for a tick, and `autoUpdate` re-subscribed. None of it is visible
 * or avoidable from the adopter's call site, and a `null` → SAME node
 * transition is not a node swap.
 *
 * So a `null` floating ref does not detach where it is read. The detach is
 * DEFERRED one microtask and CANCELLED when the ref comes back as the same node
 * (against the same reference) before that microtask runs. Everything else is
 * unchanged: a genuinely different node still swaps synchronously — detach the
 * old, attach the new — and a `null` that is still `null` when the microtask
 * runs detaches. A MICROTASK and not a timer, because the re-set lands inside
 * the same patch: the two are one tick apart by construction, while a timer
 * would leave a genuinely unmounted node attached for a frame.
 *
 * Close and scope disposal detach IMMEDIATELY, never through the deferral, so
 * a surface that closes mid-cycle releases its listeners in the same tick. Each
 * deferral carries a token that `detach()` clears, so a microtask queued before
 * a close does nothing when it runs and can never reach the NEXT open's session.
 *
 * The tolerance is on the ATTACHMENT only. Registration stays keyed on `open`
 * (D6), and an adopter's `open` MUST NOT derive from a ref that cycles:
 * `open: () => panelRef.value !== null` makes registration follow the ref, the
 * style follow the entry, and the render that binds the style re-set the ref —
 * 'Maximum recursive updates exceeded'. A component whose life IS the open
 * passes the literal `true`, which is what the inspector does.
 *
 * ── A NESTED OVERLAY NEVER RE-RENDERS ITS HOST (th-rjgza) ──────────────────
 *
 * A child's scope disposal runs INSIDE its host's render effect with tracking
 * on (the host's patch is what unmounts it), so whatever the teardown read
 * subscribed the host to what it then wrote — and a host that remounts its
 * child on every render (the inspector under @vue/test-utils' uncached Teleport
 * stub) re-rendered without end. So the lifecycle reads only RAW state —
 * `registered` here, the registry's raw `ledger` there — and writes `entry` and
 * the ledger for their readers alone; and `style` reads the ONE `zIndex`
 * computed rather than walking the ledger itself, so a ledger change that
 * leaves this surface's own z alone (a child opening, revealing or closing
 * inside it) stops at an unchanged number and re-renders nothing.
 *
 * ── HIDDEN IS A REGISTRY STATE, AND THE PAIR IS SPELLED HERE ───────────────
 *
 * While `visible` reads false the entry's `state` getter answers
 * `OVERLAY_STATE_HIDDEN`, so the registry drops it from `obstaclesFor` and from
 * Escape dispatch while it keeps its band and slot, and `style`
 * spreads `{ visibility: 'hidden', pointerEvents: 'none' }` (lead decision 4).
 * That pair is spelled ONCE, in `HIDDEN_STYLE` below, and in no adapter: the
 * adapters pass booleans, which is what keeps the Ruby band-vocabulary ratchet
 * green on the three positioning adapters.
 *
 * ── WHAT RE-PLACES ON `touch` ──────────────────────────────────────────────
 *
 * EVERY open placed overlay re-places when `overlayVersion` bumps, not only
 * the ones activated after the toucher. Why: `touch` records no toucher (the
 * registry's seam is one counter), and selecting by activation would need a
 * second query the registry does not have. Correctness needs no selection —
 * `obstaclesFor` looks only BACKWARD in activation order, so an
 * earlier-activated overlay's obstacle set cannot contain the toucher and its
 * re-place recomputes exactly where it already is; only the later-activated
 * overlays actually move, which is the spec's guarantee ("re-places every
 * later-activated overlay"). The cost is one `computePosition` per open
 * placed overlay per touch — a handful at most, on a `moveend`, not a
 * `mousemove`.
 *
 * WHO IS AVOIDED is also decided here, not in the engine: the engine runs
 * `avoidOverlays` for every kind and the middleware passes through when it is
 * handed nothing, so a pass hands it `obstaclesFor(entry)` — read at placement
 * time, as rects — only when the kind carries `avoids: true`. The registry
 * decides who is IN that set (visible, obstacle, earlier-activated, the
 * nesting exemption); the roster decides whether this kind looks at it at all.
 *
 * ── A CUE'S OWN BOX, AND THE STYLE-ARRAY IDIOM ─────────────────────────────
 *
 * `style` never carries `width` or `height`. A kind that sizes itself (a cue —
 * the dwell ring's diameter, the bow glyph) spreads its own box as the NEXT
 * member of a style array, `:style="[style, { width, height }]"`, and so does
 * every other adopter-owned declaration (the lane card's `pointerEvents:
 * 'auto'` armor while revealed, the cursor card's reveal fade). The primitive's
 * member stays the only author of position, bounds and z; the merge cannot
 * collide because the two members never name the same property. Rejected: a
 * `box` option, which would be a per-kind knob validated per kind for a value
 * only the adopter knows.
 *
 * ── THE PER-KIND POLICIES: `installPolicies` ───────────────────────────────
 *
 * Outside-click and focus policies differ PER KIND by design, and the primitive
 * unifies NEITHER (stress-test Blind Spot 7): it applies exactly what the roster
 * declares and invents nothing. `installPolicies({ entry, floating, roster,
 * dismiss, options })` is called from `attach()` — the surface open and its
 * floating node in the DOM, the two facts every policy needs — and its teardown
 * runs from `detach()`: on close, on unmount, and whenever the node is swapped.
 * Every listener and every timer below is created there and released there;
 * nothing else in the lifecycle changes (lead decision 6).
 *
 *   INSIDE OR OUTSIDE IS READ FROM THE EVENT'S DISPATCH PATH, in both
 *     outside-click policies (`startedInside(event, node)`): `node` is on
 *     `event.composedPath()`. The path is fixed when the event is dispatched;
 *     the live tree is not, because a control may re-render the node that was
 *     pressed before the `document` listener runs — Vue's flush is a microtask,
 *     and the same checkpoint BETWEEN the listener invocations of one dispatch
 *     that the macrotask below guards against runs it. A wrapped Unlock label
 *     drops its later part spans the moment it is pressed, and
 *     `node.contains(event.target)` would then read a press on one of them as
 *     outside its own panel. `contains` is the fallback only where there is no
 *     path to read: an event read after its dispatch has ended (the path is
 *     empty by then) or an event object without the method.
 *
 *   `dismissOnOutside: 'panel'` (the inspector) — ONE bubble-phase `click`
 *     listener on `document`, ARMED ON A MACROTASK (`setTimeout`), then
 *     not started inside `floating` → `dismiss()`. The macrotask is load-bearing
 *     and a microtask is NOT enough: in a browser the click that opened the
 *     panel can still be propagating when the panel mounts, because a microtask
 *     checkpoint runs BETWEEN the listener invocations of one dispatch, so a
 *     `document` listener added at that moment still receives the in-flight
 *     event and closes on the click that opened it. A timer boundary is after
 *     the whole dispatch by construction. jsdom dispatches on a single stack and
 *     cannot reproduce it, which is why it is written down here rather than left
 *     to read as ceremony.
 *
 *     THE CLICK THAT REPLACES WHAT IT SHOWS CANNOT CLOSE IT EITHER (th-dw7mxs).
 *     An owner that shows a new open inside a click — a second avatar cluster's
 *     popup, a second marker's Peek — re-attaches its surface (a new reference)
 *     or mounts a new one, and that click then reaches this listener. Whether the
 *     re-attach has happened by then depends on the dispatch: a browser runs
 *     Vue's flush in the microtask checkpoint between the opener's listener and
 *     this one, so the old installation is already gone; a dispatch on one stack
 *     (page script, `element.click()`, jsdom) runs no checkpoint, so it still
 *     answers, and its `dismiss` closes whatever its owner holds by then — the
 *     replacement. So the listener reads only WHERE the press started (the path
 *     is gone once the dispatch ends) and DECIDES after the click's own updates
 *     have flushed (`nextTick`), closing only while the installation that heard
 *     the click is still live: a dismissal closes the open it was installed for
 *     and never its replacement, on every dispatch, for every owner, with no
 *     owner code. A plain outside click changes nothing it attaches to, so it
 *     closes one microtask later than it is heard. One decision per flush: the
 *     clicks dispatched on one stack (a scripted double-click) close once.
 *
 *   `dismissOnOutside: 'capture'` (the Artemis floating input) — a
 *     CAPTURE-phase `pointerdown` listener on `document`, armed after 100 ms. It
 *     stands down for the secondary button (`event.button === 2`: the
 *     workspace's own contextmenu REPOSITIONS that surface rather than closing
 *     it) and for a target inside `options.outsideExempt` — a SELECTOR the
 *     adopter supplies, resolved at EVENT time and never at attach, because the
 *     exempt node (the mention search's teleport target) mounts and unmounts
 *     while the input stands. It decides at once: a capture-phase `document`
 *     listener hears the press before any handler of the gesture runs, so no
 *     gesture can have replaced what it shows yet.
 *
 *   `dismissOnOutside: false` (the member popover, and every other kind) — NO
 *     listener at all: a composer must not vanish on a stray click.
 *
 *   `trapFocus` (the inspector) — a `keydown` on `document`, NOT on the floating
 *     node, and that binding is the portal's doing: the panel is teleported to
 *     the end of `<body>`, so focus can be sitting OUTSIDE it while it is up and
 *     a node-bound handler would never see the Tab that walks off the end of the
 *     document. It runs at the BOUNDARIES only — the browser's own sequential
 *     navigation is correct in the middle — re-querying the panel's controls on
 *     every Tab (the cycle changes while the panel is open), keeping focus on a
 *     panel with no controls at all (which needs the panel root to carry
 *     `tabindex="-1"`, as the inspector's does), wrapping last→first and
 *     first→last under Shift, and re-entering at the shift-implied edge when
 *     focus sits outside. Focus RESTORE is not here: it is conditional on
 *     whether the panel still held focus, which only the adopter knows.
 *
 * Escape is not a policy a kind spells twice: the roster's `escape` trait puts
 * an open overlay in the registry's dismissal order, and the `dismiss` handed to
 * this call is what the dispatcher invokes there. An adopter of a dismissable
 * kind therefore writes no Escape code at all — no local binding, no
 * `useEscapeDismiss` call — and the three stand-downs, the consumption and the
 * activation ordering are the registry's (see `dismiss` in the signature).
 *
 * ── SCOPE ──────────────────────────────────────────────────────────────────
 *
 * A call outside an effect scope throws a `TypeError`, exactly as
 * `useEscapeDismiss` does and for the same reason: a stale entry holds a z
 * slot, an obstacle rect and an Escape claim that nothing would ever remove. Registration runs inside the CALLING scope on purpose
 * (`scope.run`), never inside whichever scope happens to be active when `open`
 * flips: the registry binds its own `onScopeDispose` to the active scope, and a
 * flip triggered from another component's setup would otherwise tie this
 * entry's life to that component. Vue APIs within the declared `^3.3.8` range
 * only — `toValue` is 3.3; no `useTemplateRef`, no `Teleport defer`.
 *
 * @see openspec/changes/overlay-primitive/design.md — D1, D2, D2b, D3, D5, D6
 * @see openspec/changes/overlay-primitive/specs/overlay-primitive/spec.md —
 *   "One overlay primitive owns placement…", "Visibility is a registry state"
 */

import {
  computed,
  getCurrentInstance,
  getCurrentScope,
  inject,
  nextTick,
  onScopeDispose,
  provide,
  shallowRef,
  toValue,
  watch,
} from "vue";

import { BUDGET_TOKENS, bandDepth, kindFor } from "./overlay/overlayKinds";
import {
  computeBudget,
  laneBudget,
  placeOverlay,
  placementArea,
  roomierSideBudget,
  subscribeAutoUpdate,
  viewportBudget,
} from "./overlay/overlayPlacement";
import {
  OVERLAY_STATE_HIDDEN,
  OVERLAY_STATE_OPEN,
  deregister,
  obstaclesFor,
  overlayVersion,
  register,
  supersedeCards,
  zIndexFor,
} from "./overlay/overlayRegistry";

/**
 * The injection key of the parent seam. Its value is the host's `entry`
 * shallowRef — the HOLDER, read at the descendant's registration time.
 */
export const OVERLAY_PARENT_ENTRY = Symbol("overlayParentEntry");

/** The hidden pair (D5), spelled once. */
const HIDDEN_STYLE = Object.freeze({ visibility: "hidden", pointerEvents: "none" });

/** Roster token → the engine's pre-pass. `null` (unbounded) has no function. */
const BUDGET_FUNCTIONS = Object.freeze({
  "roomier-side": roomierSideBudget,
  lane: laneBudget,
  viewport: viewportBudget,
});

const NOOP = () => {};

const px = (value) => `${value}px`;

/**
 * The budget a call resolves to: the call's own when given (a function, a
 * token, or `null`), else the kind's token, else unbounded.
 */
function resolveBudget(budget, roster) {
  const source = budget === undefined ? roster.budget : budget;
  if (source === null || typeof source === "function") return source;
  if (typeof source === "string" && Object.prototype.hasOwnProperty.call(BUDGET_FUNCTIONS, source)) {
    return BUDGET_FUNCTIONS[source];
  }
  throw new TypeError(
    `useOverlay: \`budget\` must be a function, null, or one of ${JSON.stringify(
      BUDGET_TOKENS.filter(Boolean),
    )}; got ${JSON.stringify(source)}.`,
  );
}

/**
 * The focusable cycle a trapped panel wraps. Ported VERBATIM from
 * `ElementInspector.vue`'s `FOCUSABLE_SELECTOR`; a disabled control is skipped
 * because it is not in the tab order to begin with.
 */
const FOCUSABLE_SELECTOR = [
  "a[href]",
  "button:not([disabled])",
  "input:not([disabled])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  '[tabindex]:not([tabindex="-1"])',
].join(", ");

/**
 * THE CYCLE IS THE BROWSER'S OWN SEQUENTIAL ORDER, so a control the browser would
 * never stop on is never a boundary of it. The selector alone cannot say that: a
 * `<button tabindex="-1">` matches `button:not([disabled])` (a roving tablist's
 * unselected tabs, th-kxn6c.7.3), and a control inside a subtree that is not
 * rendered matches everything (a Dossier's hidden tab bodies, which `v-show`
 * keeps mounted). Either one taken as `last` lets Tab walk past the real last
 * stop and out of an open panel. Hidden is read from the two things a component
 * sets — the `hidden` attribute and an inline `display: none` — rather than from
 * layout, which jsdom does not compute.
 *
 * AN INERT SUBTREE IS NOT IN THE SEQUENCE EITHER (th-kxn6c.8.3). While a Dossier
 * layer is pushed, the box it covers stays rendered and `inert` for the ~300 ms
 * the push moves (the panel-layer primitive), and the browser skips every control
 * under it; read as a boundary, the covered box's last control would be where Tab
 * wraps from. The attribute is read rather than the `inert` property, which a
 * browser reflects onto it and jsdom does not implement.
 */
function inTabSequence(control, floating) {
  if (control.getAttribute("tabindex") === "-1") return false;
  for (let node = control; node && node !== floating; node = node.parentElement) {
    if (node.hidden || node.hasAttribute("inert") || node.style?.display === "none") return false;
  }
  return true;
}

/**
 * Whether `event` started inside `node`, read from the dispatch path rather
 * than the live tree (the header says why). `contains` answers only when there
 * is no path: read after the dispatch has ended, or no `composedPath` at all.
 */
function startedInside(event, node) {
  const path = typeof event.composedPath === "function" ? event.composedPath() : [];
  return path.length > 0 ? path.includes(node) : node.contains(event.target);
}

/** The 'capture' policy's arming delay, from `ArtemisFloatingInput.vue`. */
const CAPTURE_ARM_MS = 100;

/**
 * `dismissOnOutside: 'panel'`. Ported from `ElementInspector.vue`'s
 * `dismissOnOutsideClick` and the `armDismissal` timer that installs it. The
 * decision waits for the click's own updates and closes only while this
 * installation is live (the header's th-dw7mxs paragraph).
 */
function installPanelDismissal(floating, dismiss) {
  let live = true;
  let deciding = false;

  const onClick = (event) => {
    // Where the press started is read NOW: the dispatch path is gone once it ends.
    if (deciding || startedInside(event, floating)) return;
    deciding = true;
    nextTick(() => {
      deciding = false;
      // Torn down by the click's own updates (a replacement re-attached or
      // unmounted this surface, or its owner closed it): not this open's click.
      if (live) dismiss();
    });
  };

  // A MACROTASK, not a microtask — the header says why, and jsdom cannot show it.
  const armed = setTimeout(() => {
    document.addEventListener("click", onClick);
  });

  return () => {
    live = false;
    clearTimeout(armed);
    document.removeEventListener("click", onClick);
  };
}

/**
 * `dismissOnOutside: 'capture'`. Ported from `ArtemisFloatingInput.vue`'s
 * `handleClickOutside` and its `clickOutsideTimer`. The source's second branch —
 * collapsing its attachment indicator on a click INSIDE the popover — stays with
 * that adapter: it is the component's own state, not a dismissal policy.
 */
function installCaptureDismissal(floating, dismiss, exemptSelector) {
  const onPointerDown = (event) => {
    // The secondary button belongs to the workspace's contextmenu, which
    // REPOSITIONS this surface rather than closing it.
    if (event.button === 2) return;

    // Resolved per EVENT, never at attach: the exempt node mounts and unmounts
    // while the surface stands (the source resolves its teleport target by id
    // inside the handler for exactly this reason).
    if (exemptSelector) {
      const exempt = document.querySelector(exemptSelector);
      if (exempt && startedInside(event, exempt)) return;
    }

    if (!startedInside(event, floating)) dismiss();
  };

  let armed = setTimeout(() => {
    armed = null;
    document.addEventListener("pointerdown", onPointerDown, true);
  }, CAPTURE_ARM_MS);

  return () => {
    if (armed) clearTimeout(armed);
    armed = null;
    document.removeEventListener("pointerdown", onPointerDown, true);
  };
}

/**
 * `trapFocus`. Ported from `ElementInspector.vue`'s `focusableControls`,
 * `trapTab` and the `handleKeydown` that routes Tab into it — including its
 * binding at the DOCUMENT, which the portal requires (see the header). The
 * source's menu-navigation branch is adapter-local and stays there.
 */
function installFocusTrap(floating) {
  const trapTab = (event) => {
    // Queried LIVE on every Tab rather than cached at open, because the cycle
    // changes while the panel is up: a disclosure adds controls, and an
    // in-flight unlock disables the row the viewer just pressed. A cached list
    // would wrap around a control that is no longer there.
    const controls = Array.from(floating.querySelectorAll(FOCUSABLE_SELECTOR)).filter((control) =>
      inTabSequence(control, floating),
    );
    const active = document.activeElement;

    // A panel with no controls at all still HOLDS focus rather than releasing it
    // to <body>: letting Tab escape from there is the same keyboard trap in its
    // quietest form. `floating.focus()` needs the root to carry `tabindex="-1"`,
    // which every trapped adopter declares.
    if (controls.length === 0) {
      event.preventDefault();
      floating.focus();
      return;
    }

    const first = controls[0];
    const last = controls[controls.length - 1];

    // Focus outside an open panel — a click on an unfocusable region, or a
    // programmatic move — re-enters at the edge the Tab direction implies rather
    // than leaving the viewer stranded outside a panel that is still up.
    if (!floating.contains(active)) {
      event.preventDefault();
      (event.shiftKey ? last : first).focus();
      return;
    }

    // `active === floating` is the open's own landing spot, and it belongs to the
    // BACKWARD branch only: forward from the root, the browser's next stop is
    // already the first control inside it, so intervening would skip it.
    if (event.shiftKey && (active === first || active === floating)) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && active === last) {
      event.preventDefault();
      first.focus();
    }
  };

  const onKeydown = (event) => {
    if (event.key === "Tab") trapTab(event);
  };

  document.addEventListener("keydown", onKeydown);
  return () => document.removeEventListener("keydown", onKeydown);
}

/**
 * The per-kind policies (design D1, stress-test Blind Spot 7). See the header
 * section for what each one binds and why.
 *
 * `entry` is handed in for symmetry with the rest of the lifecycle; no policy
 * reads it, so it is not destructured here.
 *
 * @param {{ floating: Element, roster: object, dismiss: (() => void)|null,
 *   options: object }} installation
 * @returns {() => void} the teardown: every listener and timer installed above.
 */
function installPolicies({ floating, roster, dismiss, options }) {
  const teardowns = [];

  // An outside-click policy with nothing to call is not installed: `dismiss` is
  // what the outside click DOES, and the roster cannot know whether the adopter
  // supplied one. `dismissOnOutside` is the closed enum `DISMISS_ON_OUTSIDE_POLICIES`,
  // so 'capture' or 'panel' is the whole of the truthy case.
  if (roster.dismissOnOutside && typeof dismiss === "function") {
    teardowns.push(
      roster.dismissOnOutside === "capture"
        ? installCaptureDismissal(floating, dismiss, options?.outsideExempt ?? null)
        : installPanelDismissal(floating, dismiss),
    );
  }

  if (roster.trapFocus) teardowns.push(installFocusTrap(floating));

  if (teardowns.length === 0) return NOOP;
  return () => teardowns.forEach((teardown) => teardown());
}

/**
 * @param {object} call - see the header.
 * @returns {{ style: import('vue').ComputedRef<object>,
 *   side: import('vue').ComputedRef<string|null>,
 *   placement: import('vue').ComputedRef<string|null>,
 *   arrowStyle: import('vue').ComputedRef<{ x: number, left: string }|null>,
 *   zIndex: import('vue').ComputedRef<number|null>,
 *   depthClass: import('vue').ComputedRef<string|null>,
 *   entry: import('vue').ShallowRef<object|null>,
 *   replace: () => Promise<void> }}
 */
export function useOverlay({
  kind,
  open,
  reference = null,
  floating = null,
  parent,
  visible,
  dismiss = null,
  placement,
  budget,
  rect,
  options,
  active = true,
  escapeWhen = null,
  id,
} = {}) {
  const scope = getCurrentScope();
  if (!scope) {
    throw new TypeError(
      "useOverlay must be called inside an effect scope (a component setup, or an explicit effectScope()) so its registration cannot leak.",
    );
  }
  if (open === undefined) {
    throw new TypeError(
      "useOverlay({ kind, open, … }) requires an explicit `open` — registration is openness, and there is no implicitly-open overlay.",
    );
  }

  // The parent seam. Both calls are setup-only, so they run here, once, and
  // only inside a component instance; the holder is what descendants read.
  const instance = getCurrentInstance();
  const parentSource =
    parent !== undefined ? parent : instance ? inject(OVERLAY_PARENT_ENTRY, null) : null;
  const entry = shallowRef(null);

  const roster = kindFor(kind);
  // Every kind injects; only a kind that CAN HOST one provides. A cue, a chrome
  // bar, a guidance surface and a tooltip are never opened from, and their
  // adopters are routinely ancestors of most of the page — so they hand nothing
  // down and their descendants reach the hosting ancestor above them.
  if (instance && roster?.hosts) provide(OVERLAY_PARENT_ENTRY, entry);
  if (!roster) {
    // Production only: `kindFor` has logged. The surface renders unregistered.
    return {
      style: computed(() => ({})),
      side: computed(() => null),
      placement: computed(() => null),
      arrowStyle: computed(() => null),
      maxHeight: computed(() => null),
      zIndex: computed(() => null),
      depthClass: computed(() => null),
      entry,
      replace: () => Promise.resolve(),
    };
  }

  const placedKind = roster.placed;
  const budgetFn = resolveBudget(budget, roster);

  const isOpen = computed(() => toValue(open) === true);
  const isVisible = computed(() => visible === undefined || Boolean(toValue(visible)));
  const state = () => (isVisible.value ? OVERLAY_STATE_OPEN : OVERLAY_STATE_HIDDEN);

  const floatingRect = () => toValue(floating)?.getBoundingClientRect?.() ?? null;
  const entryRect = typeof rect === "function" ? rect : placedKind ? floatingRect : null;

  /** The last pass that placed, or `null` while unpositioned. */
  const placed = shallowRef(null);
  /** The budget pre-pass, bound before any measurement. */
  const prePassBudget = shallowRef(null);

  let passToken = 0;
  /** The live attachment: the floating node and reference being placed against. */
  let session = null;
  /**
   * The deferred detach a `null` floating ref queues, held as a TOKEN (see the
   * header): cancelling is one assignment, and the microtask that would run it
   * does nothing unless the token it closed over is still the pending one.
   */
  let pendingDetach = null;
  /**
   * The entry this call holds, RAW: what the lifecycle below reads. `entry` is
   * its reactive mirror for the readers that should re-run when it changes (the
   * style, the adopter, a descendant), and the lifecycle only ever WRITES it —
   * see the header, A NESTED OVERLAY NEVER RE-RENDERS ITS HOST.
   */
  let registered = null;

  const mergedOptions = () => ({ ...roster.options, ...toValue(options) });

  /**
   * The pre-pass: pure over the reference rect and the viewport (or the call's
   * `boundary`, the area the engine places in). It is handed the placement the
   * pass will ask for, so a kind with a readable minimum is capped at the side
   * the engine will decide, from its first frame.
   */
  function refreshBudget(referenceValue) {
    const referenceRect = referenceValue?.getBoundingClientRect?.();
    const merged = mergedOptions();
    prePassBudget.value = referenceRect
      ? computeBudget({
          referenceRect,
          viewport: placementArea(toValue(floating), merged),
          budget: budgetFn,
          options: merged,
          placement: toValue(placement) ?? roster.placement,
        })
      : null;
  }

  async function runPass() {
    const current = session;
    if (!current || !placedKind) return;

    passToken += 1;
    const token = passToken;
    refreshBudget(current.reference);

    // Only a kind with `avoids: true` is handed obstacles at all: the engine
    // runs `avoidOverlays` unconditionally and the middleware passes through
    // with nothing to avoid, so this is where the trait is read (spec: "The
    // engine SHALL run avoidOverlays for every kind with avoids: true").
    const self = registered;
    const obstacles = roster.avoids
      ? () => obstaclesFor(self).map((obstacle) => obstacle.rect())
      : undefined;
    const result = await placeOverlay({
      reference: current.reference,
      floating: current.floating,
      kind: roster,
      placement: toValue(placement),
      budget: budgetFn,
      obstacles,
      options: toValue(options),
    });

    if (token !== passToken || session !== current) return;
    if (result) placed.value = result;
  }

  function attach(floatingNode, referenceValue) {
    const current = { floating: floatingNode, reference: referenceValue, teardowns: [] };
    session = current;

    if (placedKind) {
      // Pass one: `autoUpdate`'s immediate call, or ours when the kind takes none.
      current.teardowns.push(subscribeAutoUpdate(referenceValue, floatingNode, roster, runPass));
      if (roster.autoUpdate === false) runPass();
      // Pass two: the box has laid out under the pre-pass cap.
      nextTick().then(() => {
        if (session === current) runPass();
      });
    }

    current.teardowns.push(
      installPolicies({
        entry: registered,
        floating: floatingNode,
        roster,
        dismiss,
        options: mergedOptions(),
      }),
    );
  }

  function detach() {
    // Whatever reason brings us here — a swap, a close, disposal — supersedes a
    // deferral: the pending microtask reads this and stands down.
    pendingDetach = null;
    if (!session) return;
    const ending = session;
    session = null;
    passToken += 1;
    ending.teardowns.forEach((teardown) => teardown());
    placed.value = null;
  }

  /**
   * The teleported-ref tolerance (see the header): a `null` floating ref waits
   * one microtask for the same node to come back. One deferral at a time is
   * enough — while it is pending nothing has detached, so a second `null` reads
   * the same live session.
   */
  function detachSoon() {
    if (!session || pendingDetach) return;
    const token = {};
    pendingDetach = token;
    queueMicrotask(() => {
      if (pendingDetach !== token) return;
      detach();
    });
  }

  function openEntry() {
    if (registered) return;
    // Inside the CALLING scope, whatever scope the flip came from (see the header).
    const handle = scope.run(() =>
      register({
        id,
        kind,
        parent: toValue(parentSource) ?? null,
        rect: entryRect,
        dismiss,
        active,
        state,
        escapeWhen,
      }),
    );
    if (!handle) return;
    registered = handle.entry;
    // ONE FLOATING PANEL AT A TIME (see the header): before `entry` is published,
    // which is what attaches and places, so the panel never lands clear of a card
    // that is closing under it.
    if (roster.supersedesCards) supersedeCards(registered);
    entry.value = registered;
    refreshBudget(toValue(reference));
  }

  function closeEntry() {
    const current = registered;
    if (!current) return;
    registered = null;
    detach();
    deregister(current);
    entry.value = null;
    prePassBudget.value = null;
  }

  // Registration rides the rising edge of `open`, synchronously (D6).
  watch(
    isOpen,
    (nowOpen) => {
      if (nowOpen) openEntry();
      else closeEntry();
    },
    { immediate: true, flush: "sync" },
  );

  // The attachment: open, with a floating node, and (for a placed kind) a
  // reference. Any of the three changing re-attaches — a template ref landing
  // after mount, a swapped node, the cursor card's next reveal rect — with the
  // one tolerance the header spells out for a teleported ref's `null` cycle.
  watch(
    [entry, () => toValue(floating) ?? null, () => toValue(reference) ?? null],
    ([current, floatingNode, referenceValue]) => {
      // The same node against the same reference as the LIVE attachment: the
      // re-set half of a patch's ref cycle, not a swap. Cancel the deferred
      // detach and leave the policies, the placement and the subscription alone.
      if (
        session &&
        current &&
        floatingNode === session.floating &&
        referenceValue === session.reference
      ) {
        pendingDetach = null;
        return;
      }

      // The node reading `null` under a surface that is still open: almost
      // always the other half of that cycle, so defer and let the re-set above
      // cancel it. A close or a disposal never arrives here — both detach
      // through `closeEntry`, synchronously.
      if (session && current && !floatingNode) {
        detachSoon();
        return;
      }

      detach();
      if (!current || !floatingNode) return;
      if (placedKind && !referenceValue) return;
      attach(floatingNode, referenceValue);
    },
    { immediate: true, flush: "sync" },
  );

  // A moved obstacle: every open placed overlay re-places (see the header).
  watch(overlayVersion, () => {
    if (session) runPass();
  });

  onScopeDispose(closeEntry);

  /**
   * The resolved cap, as the number both readers share (see the header's
   * `maxHeight`): the pass's answer once one has placed, the pre-pass budget
   * before that, `null` for unbounded and for a kind nothing here places.
   * `style` formats THIS — it does not resolve the cap a second time — so the
   * CSS the root binds and the number the card is handed are one declaration.
   */
  const maxHeight = computed(() => {
    if (!placedKind) return null;
    const pass = placed.value;
    return (pass ? pass.maxHeight : prePassBudget.value) ?? null;
  });

  /**
   * THE READABLE MINIMUM, as a floor under the box (founder ruling 26; th-kxn6c.12.36).
   * A kind that declares `readableHeight` opens AT it, from its first frame, never as
   * the bare chrome of a body that has not arrived (a Peek whose grain chunk is still
   * downloading measured 2 px: its two borders). It is the same number that decided the
   * side, never more than the cap the side has, so the floor can never push the box
   * past its room; and it is read off the resolved cap, so it follows each pass's.
   * Growth past it is the body's own, and the resize observer re-places for it once.
   * A closed surface states no floor, as it states no cap.
   */
  const minHeight = computed(() => {
    if (!placedKind || !isOpen.value) return null;
    const { readableHeight } = mergedOptions();
    if (!(Number.isFinite(readableHeight) && readableHeight > 0)) return null;
    const cap = maxHeight.value;
    return cap == null ? readableHeight : Math.min(readableHeight, cap);
  });

  // This surface's z, derived ONCE. `style` reads it rather than asking the
  // registry again, so a ledger change that leaves the number alone stops here
  // (header, A NESTED OVERLAY NEVER RE-RENDERS ITS HOST).
  const zIndex = computed(() => (entry.value ? zIndexFor(entry.value) : null));
  const depthClass = computed(() => bandDepth(roster.band));

  const style = computed(() => {
    const result = {};
    if (placedKind) {
      result.position = "fixed";
      const pass = placed.value;
      if (pass) {
        result.top = px(pass.top);
        result.left = px(pass.left);
      }
      if (maxHeight.value != null) result.maxHeight = px(maxHeight.value);
      if (minHeight.value != null) result.minHeight = px(minHeight.value);
      if (pass?.maxWidth != null) result.maxWidth = px(pass.maxWidth);
    }
    if (zIndex.value != null) result.zIndex = zIndex.value;
    if (!isVisible.value) Object.assign(result, HIDDEN_STYLE);
    return result;
  });

  const side = computed(() => placed.value?.side ?? null);

  // The candidate the chain settled on, beside the side it faces. Read from the
  // SAME pass result, so the two can never describe different frames.
  const resolvedPlacement = computed(() => placed.value?.placement ?? null);

  const arrowStyle = computed(() => {
    const arrow = placed.value?.arrow;
    return arrow ? { x: arrow.x, left: px(arrow.x) } : null;
  });

  return {
    style,
    side,
    placement: resolvedPlacement,
    arrowStyle,
    maxHeight,
    zIndex,
    depthClass,
    entry,
    replace: runPass,
  };
}
